import { supabase } from '../lib/supabase';
import { activityService } from './activityService';
import { notificationService } from './notificationService';
import { DEMO_ERROR_MESSAGE, isDemoUser } from '../utils/demoMode';

/**
 * Normalize a rams_documents row to match the shape DocumentsTab expects
 * from the documents table. Used by the site detail page to merge H&S Docs
 * (uploaded via /rams-management) into the Documents tab display. Without
 * this merge, anything written to public.rams_documents is invisible on
 * every page except /rams-management itself. See GIVolooT.
 *
 * IDs are prefixed with 'rams:' so React keys stay unique when concatenated
 * with documents-table rows. The renderer checks this prefix to skip the
 * per-document detail-page navigation (the detail route only looks up by
 * documents.id today — a follow-up under 5m1glArl consolidates storage).
 *
 * Defaults: category='rams' (truthful — these always are H&S Docs),
 * status='valid' (rams_documents has no expiry / status tracking),
 * expiry_date=null, owner=null. The renderer must tolerate these.
 */
export const normalizeRamsDocument = (ramsRow) => {
  if (!ramsRow) return null;
  return {
    id: `rams:${ramsRow?.id}`,
    title: ramsRow?.title || '',
    category: 'rams',
    status: 'valid',
    expiry_date: null,
    owner: null,
    site_id: ramsRow?.site_id,
    company_id: ramsRow?.company_id,
    file_path: ramsRow?.file_path || null,
    uploaded_by: ramsRow?.uploaded_by,
    created_at: ramsRow?.created_at,
    source: 'rams_documents',
  };
};

export const documentService = {
  // Default-filters soft-deleted documents out (deleted_at IS NULL). Pass
  // `{ includeDeleted: true }` from the document-management "View deleted"
  // tab to surface the full set; the page then narrows to only the
  // soft-deleted rows for display. Mirrors PR 1's archived-sites opt-in
  // shape exactly (this is the canonical NSherjFh soft-delete read
  // pattern; reuse for rams_documents, snags, diaries, etc.).
  async getDocumentsBySite(siteId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase?.from('documents')?.select('*')?.eq('site_id', siteId)?.order('created_at', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async getAllDocuments(options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase?.from('documents')?.select('*')?.order('created_at', { ascending: false });
      if (!includeDeleted) query = query?.is('deleted_at', null);
      const { data, error } = await query;

      if (error) throw error;
      return { data: data || [], error: null };
    } catch (error) {
      return { data: [], error: { message: error?.message } };
    }
  },

  async createDocument(documentData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Demo mode guard — surface an explicit error instead of fake success.
      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: { message: DEMO_ERROR_MESSAGE } };
      }

      const { data, error } = await supabase?.from('documents')?.insert(documentData)?.select()?.single();

      if (error) throw error;

      // Log activity
      if (data?.site_id) {
        await activityService?.createActivity({
          companyId: data?.company_id,
          siteId: data?.site_id,
          activityType: 'document_uploaded',
          title: `Document uploaded: ${data?.title}`,
          description: `Category: ${data?.category || 'General'}${data?.expiry_date ? ' | Expires: ' + new Date(data?.expiry_date)?.toLocaleDateString() : ''}`,
          relatedEntityId: data?.id,
          metadata: { documentId: data?.id, category: data?.category, mimeType: data?.mime_type }
        });
      }

      // In-app notification if document expires within 30 days
      if (data?.expiry_date) {
        const expiryDate = new Date(data?.expiry_date);
        const daysUntilExpiry = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));
        if (daysUntilExpiry <= 30 && daysUntilExpiry >= 0) {
          notificationService?.createNotification(
            user?.id,
            'document_expiring',
            'Document Expiring Soon',
            `"${data?.title}" expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}`,
            '/document-management',
            data?.id,
            data?.site_id
          )?.catch(() => {});
        }
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async updateDocument(documentId, updates) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      // Demo mode guard — surface an explicit error instead of fake success.
      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: { message: DEMO_ERROR_MESSAGE } };
      }
      const { data, error } = await supabase?.from('documents')?.update(updates)?.eq('id', documentId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // Soft-delete: stamps deleted_at + deleted_by, leaves the row (and its
  // storage file) intact so restoreDocument can resurface it. The `.is(
  // 'deleted_at', null)` predicate on the update makes it idempotent — a
  // second click on an already-deleted doc is a no-op (returns null data
  // with no error) instead of overwriting the original deleted_by
  // attribution. The `update_site_compliance` trigger fires on this
  // UPDATE and re-rolls site compliance with the deleted doc excluded.
  async deleteDocument(documentId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      // Demo mode guard — surface an explicit error instead of fake success.
      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: { message: DEMO_ERROR_MESSAGE } };
      }
      const { data, error } = await supabase
        ?.from('documents')
        ?.update({
          deleted_at: new Date().toISOString(),
          deleted_by: user?.id,
        })
        ?.eq('id', documentId)
        ?.is('deleted_at', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  // Inverse of deleteDocument: clears deleted_at + deleted_by so the doc
  // reappears in default queries and the compliance trigger re-counts it.
  // The `.not('deleted_at', 'is', null)` predicate keeps this idempotent
  // (no-op on an already-active row).
  async restoreDocument(documentId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');
      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: { message: DEMO_ERROR_MESSAGE } };
      }
      const { data, error } = await supabase
        ?.from('documents')
        ?.update({
          deleted_at: null,
          deleted_by: null,
        })
        ?.eq('id', documentId)
        ?.not('deleted_at', 'is', null)
        ?.select()
        ?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async getDocumentById(documentId, options = {}) {
    const { includeDeleted = false } = options;
    try {
      let query = supabase?.from('documents')?.select('*')?.eq('id', documentId);
      if (!includeDeleted) query = query?.is('deleted_at', null);
      // maybeSingle so a soft-deleted ID returns null gracefully (the
      // detail page renders a not-found state) instead of throwing
      // PGRST116.
      const { data, error } = await query?.maybeSingle();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async getDocumentFileUrl(filePath) {
    try {
      // Try site-documents bucket first, fall back to construction-documents
      let result = await supabase?.storage?.from('site-documents')?.createSignedUrl(filePath, 3600);
      if (result?.error) {
        result = await supabase?.storage?.from('construction-documents')?.createSignedUrl(filePath, 3600);
      }
      if (result?.error) throw result?.error;
      return { url: result?.data?.signedUrl, error: null };
    } catch (error) {
      return { url: null, error: { message: error?.message } };
    }
  },

  async uploadDocumentFile(file, documentId, companyId, siteId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Validate file type
      const validTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/png',
        'image/jpg'
      ];
      
      if (!validTypes?.includes(file?.type)) {
        throw new Error('Invalid file type. Only PDF, DOC, DOCX, JPG, and PNG files are allowed');
      }

      // Validate file size (10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file?.size > maxSize) {
        throw new Error('File size must be less than 10MB');
      }

      // Generate unique file path
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
      const filePath = `${companyId}/${siteId}/documents/${fileName}`;

      // Upload to storage
      const { error: uploadError } = await supabase?.storage?.from('construction-documents')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

      if (uploadError) {
        const statusCode = String(uploadError?.statusCode ?? '');
        const msgStr = String(uploadError?.message ?? '');
        if (statusCode === '413' || /exceeded.*size|too large|payload too large/i.test(msgStr)) {
          const friendly = new Error('Document file is too large. Maximum size is 10MB.');
          friendly.code = 'PAYLOAD_TOO_LARGE';
          friendly.statusCode = 413;
          friendly.cause = uploadError;
          throw friendly;
        }
        throw uploadError;
      }

      // Update document record with file info
      const { data, error } = await supabase?.from('documents')?.update({
        file_path: filePath,
        file_size: file?.size,
        mime_type: file?.type,
        uploaded_by: user?.id
      })?.eq('id', documentId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async replaceDocumentFile(file, documentId, companyId, siteId, oldFilePath) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Validate file type
      const validTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/png',
        'image/jpg'
      ];
      
      if (!validTypes?.includes(file?.type)) {
        throw new Error('Invalid file type. Only PDF, DOC, DOCX, JPG, and PNG files are allowed');
      }

      // Validate file size (10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file?.size > maxSize) {
        throw new Error('File size must be less than 10MB');
      }

      // Generate unique file path
      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substring(7)}.${fileExt}`;
      const filePath = `${companyId}/${siteId}/documents/${fileName}`;

      // Upload new file to storage
      const { error: uploadError } = await supabase?.storage?.from('construction-documents')?.upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

      if (uploadError) throw uploadError;

      // Update document record with new file info
      const { data, error } = await supabase?.from('documents')?.update({
        file_path: filePath,
        file_size: file?.size,
        mime_type: file?.type,
        uploaded_by: user?.id
      })?.eq('id', documentId)?.select()?.single();

      if (error) throw error;

      // Note: Old file remains in storage (safe approach to avoid 404s)
      // Could optionally delete old file here if needed

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message } };
    }
  },

  async uploadDocument({ file, title, category, owner, issueDate, expiryDate, notes, siteId, companyId, onProgress, certificateNumber, policyNumber, contractorName, inspectionDate }) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Validate file type
      const validTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/png',
        'image/jpg'
      ];
      if (!validTypes?.includes(file?.type)) {
        throw new Error('Invalid file type. Only PDF, DOC, DOCX, JPG, and PNG files are allowed');
      }

      // Validate file size (10MB)
      const maxSize = 10 * 1024 * 1024;
      if (file?.size > maxSize) {
        throw new Error('File size must be less than 10MB');
      }

      // Generate file path
      const timestamp = Date.now();
      const safeFileName = file?.name?.replace(/[^a-zA-Z0-9._-]/g, '_');
      const filePath = `${siteId}/documents/${timestamp}-${safeFileName}`;

      // Get Supabase storage URL and anon key for XHR upload with real progress
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
      const { data: { session } } = await supabase?.auth?.getSession();
      const authToken = session?.access_token || supabaseKey;

      // Upload via XHR for real progress tracking
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const uploadUrl = `${supabaseUrl}/storage/v1/object/site-documents/${filePath}`;

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && onProgress) {
            const percent = Math.round((event.loaded / event.total) * 90);
            onProgress(percent);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            let errMsg = `Storage upload failed (${xhr.status})`;
            try {
              const resp = JSON.parse(xhr.responseText);
              errMsg = resp?.message || resp?.error || errMsg;
            } catch (_) {}
            const err = new Error(errMsg);
            err.code = String(xhr.status);
            reject(err);
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during file upload. Please check your connection.'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload was cancelled.'));
        });

        xhr.open('POST', uploadUrl);
        xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
        xhr.setRequestHeader('apikey', supabaseKey);
        xhr.setRequestHeader('x-upsert', 'false');
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

        xhr.send(file);
      });

      // Signal DB insert phase
      if (onProgress) onProgress(95);

      // Insert document record
      const { data, error } = await supabase?.from('documents')?.insert({
        site_id: siteId,
        company_id: companyId,
        title,
        file_name: file?.name,
        file_path: filePath,
        file_size: file?.size,
        mime_type: file?.type,
        category: category,
        owner: owner || '',
        issue_date: issueDate || new Date()?.toISOString()?.split('T')?.[0],
        expiry_date: expiryDate || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0],
        notes: notes || null,
        uploaded_by: user?.id,
        certificate_number: certificateNumber || null,
        policy_number: policyNumber || null,
        contractor_name: contractorName || null,
        inspection_date: inspectionDate || null
      })?.select()?.single();

      if (error) throw error;

      if (onProgress) onProgress(100);

      // Log activity
      if (data?.site_id) {
        await activityService?.createActivity({
          companyId: data?.company_id,
          siteId: data?.site_id,
          activityType: 'document_uploaded',
          title: `Document uploaded: ${data?.title}`,
          description: `Category: ${data?.category || 'General'}${data?.expiry_date ? ' | Expires: ' + new Date(data?.expiry_date)?.toLocaleDateString() : ''}`,
          relatedEntityId: data?.id,
          metadata: { documentId: data?.id, category: data?.category, mimeType: data?.mime_type }
        });
      }

      // In-app notification if document expires within 30 days
      if (data?.expiry_date) {
        const expiryDate = new Date(data?.expiry_date);
        const daysUntilExpiry = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));
        if (daysUntilExpiry <= 30 && daysUntilExpiry >= 0) {
          notificationService?.createNotification(
            user?.id,
            'document_expiring',
            'Document Expiring Soon',
            `"${data?.title}" expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}`,
            '/document-management',
            data?.id,
            data?.site_id
          )?.catch(() => {});
        }
      }

      return { data, error: null };
    } catch (error) {
      return { data: null, error: { message: error?.message, code: error?.code } };
    }
  }
};
